import {
  getSpecialistAssignmentByRoute,
  getSpecialistOfferingsForAssignment,
  getSpecialistPerson,
  resolveSpecialistServiceCategoriesForAssignment,
  getSpecialistServicesForAssignment,
  getSpecialistWorkplace,
} from "@/app/specialists/catalog";
import { buildSpecialistProfileReviews } from "@/app/specialists/profileFixtures";
import {
  resolveFrontendFixtureSpecialistReviewFacetPublication,
  resolveFrontendFixtureSpecialistReviewsOwnerScope,
} from "@/app/specialists/reviewFacetPublication";
import {
  getSpecialistPackagesForAssignment,
  resolveSpecialistPackageCategoriesForAssignment,
} from "@/app/specialists/packages";
import { getSpecialistProfilePortfolioItems } from "@/app/specialists/profilePortfolioFixtures";
import { getSpecialistWorkplacePresentation } from "@/app/specialists/profilePresentation";
import { resolveSpecialistOnlineBookingPolicy } from "@/app/specialists/onlineBooking";
import { resolveLocationProfileSnapshot } from "@/app/locations/locationProfile";
import {
  bindLocationServicePortfolioImagesToCatalog,
  filterLocationServicePortfolioImagesByServiceIds,
  resolveLocationSnapshotServicePortfolioImages,
} from "@/app/locations/locationServicePortfolio";
import type { LocationServicePortfolioImage } from "@/app/locations/locationServicePortfolio";

export type SpecialistServicePortfolioResolver = (
  locationId: string,
) => readonly LocationServicePortfolioImage[];

export const specialistServicePortfolioFixtureResolver: SpecialistServicePortfolioResolver =
  (locationId) => {
    const profile = resolveLocationProfileSnapshot(locationId);
    if (
      profile?.locationId !== locationId ||
      !profile.bookableCatalog ||
      profile.bookableCatalog.locationId !== locationId
    ) {
      return Object.freeze([]);
    }

    return resolveLocationSnapshotServicePortfolioImages(profile);
  };

/**
 * Resolves only a public route-owned specialist, person, and workplace
 * identity. Metadata uses this same visibility gate, so a hidden assignment
 * receives generic fallback metadata without exposing its identity.
 */
export function resolveSpecialistRouteIdentity(
  locationId: string,
  userLocationId: string,
) {
  const assignment = getSpecialistAssignmentByRoute(locationId, userLocationId);

  if (!assignment || !assignment.isPublicProfileVisible) {
    return null;
  }

  const person = getSpecialistPerson(assignment.personId);
  const workplace = getSpecialistWorkplace(assignment.locationId);

  if (!person || !workplace) {
    return null;
  }

  return { assignment, person, workplace };
}

/**
 * The profile and its dedicated services page share one exact assignment
 * join. A missing, duplicate, or extra service/offering makes both routes a
 * marketplace 404 instead of publishing a partial selector.
 */
export function resolveSpecialistServicesRouteData(
  locationId: string,
  userLocationId: string,
  resolveServicePortfolioImages: SpecialistServicePortfolioResolver = specialistServicePortfolioFixtureResolver,
) {
  const identity = resolveSpecialistRouteIdentity(locationId, userLocationId);

  if (!identity) {
    return null;
  }

  const { assignment, person, workplace } = identity;
  const services = getSpecialistServicesForAssignment(userLocationId);
  const offerings = getSpecialistOfferingsForAssignment(userLocationId);
  const resolvedServiceIds = new Set(
    services.map((service) => service.serviceId),
  );
  const offeringServiceIds = new Set(
    offerings.map((offering) => offering.serviceId),
  );
  const hasCompleteServiceJoin =
    resolvedServiceIds.size === assignment.serviceIds.length &&
    offeringServiceIds.size === assignment.serviceIds.length &&
    services.length === assignment.serviceIds.length &&
    offerings.length === assignment.serviceIds.length &&
    assignment.serviceIds.every(
      (serviceId) =>
        resolvedServiceIds.has(serviceId) && offeringServiceIds.has(serviceId),
    );

  const hasOnlineBookableService = offerings.some(
    (offering) =>
      resolveSpecialistOnlineBookingPolicy(assignment, offering).isBookable,
  );

  if (!hasCompleteServiceJoin || !hasOnlineBookableService) {
    return null;
  }

  const packages = workplace.offersPackages
    ? getSpecialistPackagesForAssignment(
        assignment.userLocationId,
        workplace.locationId,
      )
    : [];
  const serviceCategoryPublication =
    resolveSpecialistServiceCategoriesForAssignment(assignment.userLocationId);
  const packageCategoryPublication =
    packages.length === 0
      ? []
      : resolveSpecialistPackageCategoriesForAssignment(
          assignment.userLocationId,
          workplace.locationId,
        );

  if (!serviceCategoryPublication || !packageCategoryPublication) {
    return null;
  }

  let rawServicePortfolioImages: readonly LocationServicePortfolioImage[] = [];
  try {
    const resolved = resolveServicePortfolioImages(locationId);
    rawServicePortfolioImages = Array.isArray(resolved) ? resolved : [];
  } catch {
    rawServicePortfolioImages = [];
  }
  const locationProfile = resolveLocationProfileSnapshot(locationId);
  const completeLocationCatalog =
    locationProfile?.locationId === locationId &&
    workplace.locationId === locationId &&
    locationProfile.bookableCatalog?.locationId === locationId &&
    locationProfile.bookableCatalog.catalogVersion === workplace.catalogVersion
      ? locationProfile.bookableCatalog
      : null;
  const boundServicePortfolioImages = completeLocationCatalog
    ? bindLocationServicePortfolioImagesToCatalog(rawServicePortfolioImages, {
        catalogVersion: completeLocationCatalog.catalogVersion,
        locationId: completeLocationCatalog.locationId,
        services: completeLocationCatalog.serviceEntries.map(
          ({ service }) => service,
        ),
      })
    : Object.freeze([]);
  const servicePortfolioImages =
    filterLocationServicePortfolioImagesByServiceIds(
      boundServicePortfolioImages,
      services.map(({ serviceId }) => serviceId),
    );

  return {
    assignment,
    person,
    workplace,
    workplacePresentation: getSpecialistWorkplacePresentation(workplace),
    services,
    offerings,
    packages,
    serviceCategoryPublication,
    packageCategoryPublication,
    servicePortfolioImages,
  };
}

export function resolveSpecialistProfileRouteData(
  locationId: string,
  userLocationId: string,
) {
  const servicesData = resolveSpecialistServicesRouteData(
    locationId,
    userLocationId,
  );

  if (!servicesData) {
    return null;
  }

  const { assignment, person, workplace } = servicesData;
  const reviews = buildSpecialistProfileReviews({
    person,
    assignment,
    workplace,
  });
  const reviewsOwnerScope =
    resolveFrontendFixtureSpecialistReviewsOwnerScope({
      assignment,
      person,
      workplace,
    });
  const reviewFacetPublication =
    resolveFrontendFixtureSpecialistReviewFacetPublication({
      assignment,
      person,
      workplace,
      services: servicesData.services,
      reviews,
    });

  return {
    ...servicesData,
    portfolioItems: getSpecialistProfilePortfolioItems(person.personId),
    reviews,
    reviewsOwnerScope,
    reviewFacetPublication,
  };
}

export type SpecialistRouteIdentityData = NonNullable<
  ReturnType<typeof resolveSpecialistRouteIdentity>
>;

export type SpecialistServicesRouteData = NonNullable<
  ReturnType<typeof resolveSpecialistServicesRouteData>
>;

export type SpecialistProfileRouteData = NonNullable<
  ReturnType<typeof resolveSpecialistProfileRouteData>
>;
